import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { HasPermission } from '../../directives/has-permission/has-permission.service';
import { UrlStateService } from '../../url-state/url-state.service';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewChild } from '@angular/core';

@Component({
  selector: 'ss-tabs-slider',
  templateUrl: './tabs-slider.component.html',
  styleUrls: ['./tabs-slider.component.scss'],
  standalone: false
})
export class TabsSliderComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() items: any[] = [];
  @Input() activeTab: number = 1;
  @Input() compact = false;
  @Input() routeSlugs: Record<number, string> = {};
  @Input() routeBase: string[] | null = null;
  @Input() routeContextKeys: string[] = [];
  private routeSubscription?: Subscription;
  private destroyed = false;
  @Output() selectedTab = new EventEmitter<any>();

  @ViewChild('strip') strip?: ElementRef<HTMLElement>;

  canScrollLeft = false;
  canScrollRight = false;

  private resizeObserver?: ResizeObserver;
  private measureTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private urlState: UrlStateService
  ) {
    this.routeSubscription = this.route.paramMap.subscribe(() => this.restoreRouteTab());
  }

  private restoreRouteTab(): void {
    if (!this.routeBase || !this.items.length) return;
    const slug = this.route.snapshot.paramMap.get('aba');
    const legacy = this.urlState.read([
      { key: 'activeTab', type: 'number', minValue: 1 },
      { key: 'tab', type: 'number', minValue: 1 },
      { key: 'aba', type: 'enum', options: Object.values(this.routeSlugs) }
    ]);
    const requested = slug || legacy['aba'];
    const legacyId = legacy['activeTab'] || legacy['tab'];
    const available = this.items.filter((item) => !item.isDisabled && (!item.permission || HasPermission.check(item.permission, item.permissionAction)));
    const tab = available.find((item) => this.routeSlugs[item.id] === requested || (!requested && item.id === legacyId)) || available[0];
    if (!tab) return;

    if ((requested && this.routeSlugs[tab.id] !== requested) || legacyId || legacy['aba']) {
      this.router.navigate([...this.routeBase, this.routeSlugs[tab.id]], { queryParams: this.urlState.read(this.routeContextKeys.map((key) => ({ key, type: 'id' }))), replaceUrl: true });
    }

    if (this.activeTab !== tab.id) {
      Promise.resolve().then(() => {
        if (this.destroyed) return;
        this.activeTab = tab.id;
        this.selectedTab.emit(tab.id);
      });
    }
  }

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(() => {
      this.measure();
      this.changeDetector.detectChanges();
    });
    this.resizeObserver.observe(this.strip!.nativeElement);
  }

  ngOnChanges() {
    this.restoreRouteTab();
    clearTimeout(this.measureTimer);
    this.measureTimer = setTimeout(() => this.measure());
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.routeSubscription?.unsubscribe();
    clearTimeout(this.measureTimer);
    this.resizeObserver?.disconnect();
  }

  changeTab(tabId: number, disabled: boolean): void {
    if (!disabled && this.routeBase && this.routeSlugs[tabId]) {
      this.router.navigate([...this.routeBase, this.routeSlugs[tabId]], { queryParams: this.urlState.read(this.routeContextKeys.map((key) => ({ key, type: 'id' }))) });
      return;
    }
    if (!disabled) {
      this.activeTab = tabId;
      this.selectedTab.emit(tabId);
    }
  }

  isTabActive(tabId: number): boolean {
    return this.activeTab === tabId;
  }

  measure() {
    const strip = this.strip?.nativeElement;
    if (!strip) return;

    this.canScrollLeft = strip.scrollLeft > 1;
    this.canScrollRight = strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1;
  }

  scroll(direction: number) {
    const strip = this.strip?.nativeElement;
    strip?.scrollBy({ left: direction * strip.clientWidth * 0.75, behavior: 'smooth' });
  }

  navigate(event: KeyboardEvent) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    const tabs = Array.from(this.strip!.nativeElement.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
    const current = tabs.indexOf(event.target as HTMLButtonElement);
    let next = current + (event.key === 'ArrowRight' ? 1 : -1);
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    next = (next + tabs.length) % tabs.length;

    event.preventDefault();
    tabs[next]?.focus();
    tabs[next]?.click();
    tabs[next]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}
